import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './ApplicantDashboard.css';

function ApplicantDashboard() {
  const [applications, setApplications] = useState([]);
  const [applicantInfo, setApplicantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [searching, setSearching] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Check for success message from job application submission
  useEffect(() => {
    if (location.state?.submissionSuccess) {
      setSuccessMessage('Your application has been submitted successfully! You can track its progress below.');
      // Clear the state to prevent showing the message on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Fetch applicant's applications on mount
  useEffect(() => {
    fetchApplications();
  }, []);

  // Check if we need to show email input form
  useEffect(() => {
    const applicantData = localStorage.getItem('applicantInfo');
    if (!applicantData && !location.state?.submissionSuccess) {
      setShowEmailForm(true);
      setLoading(false);
    }
  }, [location]);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    
    try {
      let email = null;
      let useAuthenticatedEndpoint = false;
      
      // Check if user is authenticated first
      const isAuthenticated = localStorage.getItem('isAuthenticated');
      const userData = localStorage.getItem('user');
      
      if (isAuthenticated === 'true' && userData) {
        // User is logged in, try to fetch their applications using authenticated endpoint
        try {
          const authResponse = await axios.get(
            'https://healthcare.visionkraftconsulting.com/api/applicants/my-applications',
            { withCredentials: true }
          );
          
          if (authResponse.data && authResponse.data.success) {
            setApplications(authResponse.data.applications || []);
            setApplicantInfo(authResponse.data.user);
            setLoading(false);
            return;
          }
        } catch (authErr) {
          console.log('Authenticated fetch failed, falling back to email-based search');
        }
      }
      
      // Fallback to email-based search
      const applicantData = localStorage.getItem('applicantInfo');
      if (applicantData) {
        const applicant = JSON.parse(applicantData);
        setApplicantInfo(applicant);
        email = applicant.email;
      } else if (emailInput) {
        // Use manually entered email
        email = emailInput;
        setApplicantInfo({ email: emailInput, name: '' });
      } else {
        setShowEmailForm(true);
        setLoading(false);
        return;
      }

      // Fetch applications for this applicant
      const response = await axios.get(
        `https://healthcare.visionkraftconsulting.com/api/applicants/by-email/${encodeURIComponent(email)}`
      );

      if (response.data && response.data.success) {
        setApplications(response.data.applications || []);
      } else {
        setApplications([]);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      if (err.response?.status === 404) {
        setApplications([]);
        if (!applicantInfo?.name) {
          setError('No applications found for this email address. Please check your email or submit a new application.');
        }
      } else {
        setError('Failed to load applications. Please try again later.');
      }
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  // Get application status counts
  const getStatusCounts = () => {
    return applications.reduce((counts, app) => {
      const status = app.status || 'submitted';
      counts[status] = (counts[status] || 0) + 1;
      counts.total = (counts.total || 0) + 1;
      return counts;
    }, {});
  };

  // Get latest application
  const getLatestApplication = () => {
    if (applications.length === 0) return null;
    return applications.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-success';
      case 'rejected': return 'bg-danger';
      case 'under review': return 'bg-warning';
      case 'interview scheduled': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  // Get next steps based on status
  const getNextSteps = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
        return 'Your application is being reviewed by our team. You will be contacted within 3-5 business days.';
      case 'under review':
        return 'Our hiring team is currently reviewing your application. Please be patient as we carefully consider all candidates.';
      case 'interview scheduled':
        return 'Congratulations! An interview has been scheduled. Please check your email for details.';
      case 'approved':
        return 'Congratulations! Your application has been approved. Our HR team will contact you with next steps.';
      case 'rejected':
        return 'Thank you for your interest. While we cannot move forward with your application at this time, we encourage you to apply for future opportunities.';
      default:
        return 'Your application status will be updated as it progresses through our review process.';
    }
  };

  // Handle email form submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailInput.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setSearching(true);
    setError('');
    setShowEmailForm(false);
    
    // Trigger fetchApplications with the email
    await fetchApplications();
  };

  // Clear stored data and show email form
  const handleNewSearch = () => {
    setApplications([]);
    setApplicantInfo(null);
    setEmailInput('');
    setError('');
    setSuccessMessage('');
    setShowEmailForm(true);
    localStorage.removeItem('applicantInfo');
  };

  const statusCounts = getStatusCounts();
  const latestApplication = getLatestApplication();

  if (loading || searching) {
    return (
      <div className="applicant-dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
          <h5>{searching ? 'Searching for applications...' : 'Loading your applications...'}</h5>
          <p>Please wait while we fetch your information</p>
        </div>
      </div>
    );
  }

  // Show email input form if no applicant info
  if (showEmailForm) {
    return (
      <div className="applicant-dashboard-container">
        <div className="dashboard-header email-tracking-header">
          <h1>
            <i className="bi bi-search me-3"></i>
            Track Your Application
          </h1>
          <p>Enter your email address to view your job application status with Hearts United Healthcare</p>
        </div>

        <div className="search-instructions">
          <i className="bi bi-info-circle"></i>
          <h4>How to Track Your Application</h4>
          <p>Enter the email address you used when submitting your job application. We'll show you the current status and any updates.</p>
        </div>

        <div className="application-details-section">
          <div className="section-header">
            <h3 className="section-title">
              <i className="bi bi-envelope"></i>
              Enter Your Email
            </h3>
          </div>

          {error && (
            <div className="alert-container">
              <div className="custom-alert alert-danger">
                <i className="bi bi-exclamation-circle-fill"></i>
                {error}
              </div>
            </div>
          )}

          <form onSubmit={handleEmailSubmit} className="email-input-form">
            <div className="mb-4">
              <label className="form-label h5 text-center d-block mb-3">
                <i className="bi bi-envelope me-2"></i>
                Email Address
              </label>
              <input
                type="email"
                className="form-control"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Enter the email used for your application"
                required
              />
              <div className="form-text text-center mt-2">
                Example: john.doe@email.com
              </div>
            </div>
            
            <div className="text-center">
              <button type="submit" className="action-button info me-3 mb-3">
                <i className="bi bi-search"></i>
                Track My Applications
              </button>
              <Link to="/job-application" className="action-button secondary mb-3">
                <i className="bi bi-plus-circle"></i>
                Submit New Application
              </Link>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-muted">
                <i className="bi bi-shield-check me-2"></i>
                Your information is secure and will only be used to track your application status.
              </p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="applicant-dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <h1>
          <i className="bi bi-person-badge me-3"></i>
          Welcome{applicantInfo?.name ? `, ${applicantInfo.name}` : ''}
        </h1>
        <p>Track your job application progress with Hearts United Healthcare</p>
        {applicantInfo?.email && (
          <div className="welcome-badge">
            <i className="bi bi-envelope me-2"></i>
            {applicantInfo.email}
          </div>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert-container">
          <div className="custom-alert alert-success">
            <i className="bi bi-check-circle-fill"></i>
            {successMessage}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert-container">
          <div className="custom-alert alert-danger">
            <i className="bi bi-exclamation-circle-fill"></i>
            {error}
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="status-cards-container">
        <div className="status-card">
          <div className="status-card-header">
            <div className="status-card-icon submitted">
              <i className="bi bi-file-earmark-text"></i>
            </div>
            <div>
              <h5 className="status-card-title">Total Applications</h5>
              <p className="status-card-subtitle">All submissions</p>
            </div>
          </div>
          <div className="status-card-content">{statusCounts.total || 0}</div>
          <div className="status-card-meta">
            <i className="bi bi-calendar"></i>
            {applications.length > 0 ? `Latest: ${formatDate(latestApplication?.submittedAt)}` : 'No applications yet'}
          </div>
        </div>

        <div className="status-card">
          <div className="status-card-header">
            <div className="status-card-icon review">
              <i className="bi bi-hourglass-split"></i>
            </div>
            <div>
              <h5 className="status-card-title">Under Review</h5>
              <p className="status-card-subtitle">Being processed</p>
            </div>
          </div>
          <div className="status-card-content">{statusCounts['under review'] || statusCounts.submitted || 0}</div>
          <div className="status-card-meta">
            <i className="bi bi-clock"></i>
            Average review time: 3-5 days
          </div>
        </div>

        <div className="status-card">
          <div className="status-card-header">
            <div className="status-card-icon approved">
              <i className="bi bi-check-circle"></i>
            </div>
            <div>
              <h5 className="status-card-title">Approved</h5>
              <p className="status-card-subtitle">Successful applications</p>
            </div>
          </div>
          <div className="status-card-content">{statusCounts.approved || 0}</div>
          <div className="status-card-meta">
            <i className="bi bi-trophy"></i>
            Congratulations on your success!
          </div>
        </div>
      </div>

      {/* Application Details */}
      {applications.length > 0 ? (
        <div className="application-details-section">
          <div className="section-header">
            <h3 className="section-title">
              <i className="bi bi-clipboard-data"></i>
              Application Status
            </h3>
            <button className="refresh-button" onClick={fetchApplications}>
              <i className="bi bi-arrow-clockwise"></i>
              Refresh
            </button>
          </div>

          {/* Latest Application Info */}
          {latestApplication && (
            <>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Application ID</div>
                  <div className="info-value">#{latestApplication.id}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Submitted Date</div>
                  <div className="info-value">{formatDate(latestApplication.submittedAt)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Current Status</div>
                  <div className="info-value">
                    <span className={`badge ${getStatusBadgeClass(latestApplication.status)}`}>
                      {latestApplication.status || 'Submitted'}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Position Applied</div>
                  <div className="info-value">{latestApplication.position || 'Healthcare Caregiver'}</div>
                </div>
              </div>

              {/* Application Timeline */}
              <div className="application-timeline">
                <div className="timeline-item current">
                  <div className="timeline-date">{formatDate(latestApplication.submittedAt)}</div>
                  <div className="timeline-title">Application Submitted</div>
                  <div className="timeline-description">
                    Your application has been successfully submitted and is in our system.
                  </div>
                </div>
                
                {latestApplication.status !== 'submitted' && (
                  <div className="timeline-item current">
                    <div className="timeline-date">{formatDate(latestApplication.updatedAt)}</div>
                    <div className="timeline-title">Status Update: {latestApplication.status}</div>
                    <div className="timeline-description">
                      {getNextSteps(latestApplication.status)}
                    </div>
                  </div>
                )}

                <div className="timeline-item">
                  <div className="timeline-date">Next Steps</div>
                  <div className="timeline-title">What happens next?</div>
                  <div className="timeline-description">
                    {getNextSteps(latestApplication.status)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="application-details-section">
          <div className="empty-state">
            <i className="bi bi-file-earmark-plus"></i>
            <h3>No Applications Found</h3>
            <p>You haven't submitted any job applications yet. Start your career with us today!</p>
            <Link to="/job-application" className="action-button primary">
              <i className="bi bi-plus-circle"></i>
              Submit New Application
            </Link>
          </div>
        </div>
      )}

      {/* Actions Section */}
      <div className="actions-section">
        <h4 className="mb-4">Available Actions</h4>
        <div className="actions-grid">
          <Link to="/job-application" className="action-button primary">
            <i className="bi bi-plus-circle"></i>
            New Application
          </Link>
          <button className="action-button secondary" onClick={fetchApplications}>
            <i className="bi bi-arrow-clockwise"></i>
            Refresh Status
          </button>
          <button className="action-button warning" onClick={handleNewSearch}>
            <i className="bi bi-search"></i>
            Track Different Email
          </button>
          {applications.length > 0 && (
            <button 
              className="action-button success"
              onClick={() => window.print()}
            >
              <i className="bi bi-printer"></i>
              Print Summary
            </button>
          )}
        </div>
        
        <div className="mt-4">
          <p className="text-muted">
            <i className="bi bi-info-circle me-2"></i>
            Need help? Contact our HR team at{' '}
            <a href="mailto:hr@heartsunited.com">hr@heartsunited.com</a> or call{' '}
            <a href="tel:+1-555-0123">(555) 012-3456</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ApplicantDashboard;